var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Transformer } from '@pacem/pacem-core';
;
const EMPTY_LATLNG = { lat: Number.NaN, lng: Number.NaN };
export class LatLng {
    static get Empty() {
        return EMPTY_LATLNG;
    }
    static isEmpty(l) {
        return l === EMPTY_LATLNG || (isNaN(l.lat) && isNaN(l.lng));
    }
    static isLatLng(obj) {
        return 'lat' in obj && typeof obj.lat === 'number'
            && 'lng' in obj && typeof obj.lng == 'number';
    }
    static translate(start, delta) {
        // TODO: handle 'EDGE' cases!
        return { lat: start.lat + delta.lat, lng: start.lng + delta.lng };
    }
    static equals(a, b) {
        return a === b || (a.lat === b.lat && a.lng === b.lng);
    }
}
const EMPTY_LATLNG_BOUNDS = { ne: LatLng.Empty, sw: LatLng.Empty };
;
export class LatLngBounds {
    static get Empty() {
        return EMPTY_LATLNG_BOUNDS;
    }
    static isEmpty(bnds) {
        return bnds === EMPTY_LATLNG_BOUNDS || (LatLng.isEmpty(bnds.ne) && LatLng.isEmpty(bnds.sw));
    }
    static isPoint(bnds) {
        return !this.isEmpty(bnds) && (bnds.ne === bnds.sw || (bnds.ne.lat === bnds.sw.lat && bnds.ne.lng === bnds.sw.lng));
    }
    static isLatLngBounds(obj) {
        return typeof obj === 'object'
            && 'ne' in obj && LatLng.isLatLng(obj.ne)
            && 'sw' in obj && LatLng.isLatLng(obj.sw);
    }
    static union(a, b) {
        if (LatLngBounds.isEmpty(a)) {
            if (LatLngBounds.isLatLngBounds(b)) {
                return b;
            }
            return { ne: b, sw: b };
        }
        if (LatLngBounds.isLatLngBounds(b)) {
            if (LatLngBounds.isEmpty(b)) {
                return a;
            }
            // TODO: handle 'EDGE' cases!
            const north = Math.max(a.ne.lat, b.ne.lat), east = Math.max(a.ne.lng, b.ne.lng), south = Math.min(a.sw.lat, b.sw.lat), west = Math.min(a.sw.lng, b.sw.lng);
            return { ne: { lat: north, lng: east }, sw: { lat: south, lng: west } };
        }
        if (LatLng.isLatLng(b)) {
            if (LatLng.isEmpty(b)) {
                return a;
            }
            const north = Math.max(a.ne.lat, b.lat), east = Math.max(a.ne.lng, b.lng), south = Math.min(a.sw.lat, b.lat), west = Math.min(a.sw.lng, b.lng);
            return { ne: { lat: north, lng: east }, sw: { lat: south, lng: west } };
        }
        return a;
    }
    static equals(a, b) {
        return a === b || (LatLng.equals(a.ne, b.ne) && LatLng.equals(a.sw, b.sw));
    }
}
const MapProjectionConsts = {
    SIZE: 256, // fast for computation
    RAD: Math.PI / 180,
    DEG: 180 / Math.PI,
    EPSILON: 1E-15,
    SIN_LIMIT: 1 - 1E-15,
    EARTH_RADIUS: 6_378_137,
};
const _MapConsts = {
    TRANSFORMERS_NS: 'maps',
    //DEFAULT_COORDS: { lat: 44.714188025077984, lng: 10.296516444873811 }
};
export class MapProjection {
    constructor(_pixelSize, _bounds, _normalizedSize = MapProjectionConsts.SIZE) {
        this._pixelSize = _pixelSize;
        this._bounds = _bounds;
        this._normalizedSize = _normalizedSize;
        const hSize = _normalizedSize / 2;
        this.#center = { x: hSize, y: hSize };
        this.#sizerad = _normalizedSize / 360.0;
        this.#sizedeg = hSize / Math.PI;
        this.#bottomLeft = this._latLngToWorldNormalizedPoint(_bounds.sw);
        this.#topRight = this._latLngToWorldNormalizedPoint(_bounds.ne);
    }
    #center;
    #bottomLeft;
    #topRight;
    #sizerad;
    #sizedeg;
    _latLngToWorldNormalizedPoint(latLng) {
        const x = this.#center.x + latLng.lng * this.#sizedeg;
        const latRad = latLng.lat * MapProjectionConsts.RAD;
        const limit = MapProjectionConsts.SIN_LIMIT;
        const sin = Math.sin(latRad).clamp(-limit, limit);
        const y = this.#center.y + .5 * Math.log((1 + sin) / (1 - sin)) * (-this.#sizerad);
        return { x, y };
    }
    _worldNormalizedPointToLatLng(point) {
        const lat = MapProjectionConsts.DEG * (2 * Math.atan(Math.exp((point.y - this.#center.y) / -this.#sizerad)) - Math.PI / 2);
        const lng = (point.x - this.#center.x) / this.#sizedeg;
        return { lat, lng };
    }
    _latLngToPoint(latLng) {
        const topRight = this.#topRight, bottomLeft = this.#bottomLeft, deltaLat = -topRight.y + bottomLeft.y, deltaLng = topRight.x - bottomLeft.x, target = this._latLngToWorldNormalizedPoint(latLng);
        const { width, height } = this._pixelSize;
        const x = (target.x - bottomLeft.x) * (width / deltaLng);
        const y = (target.y - topRight.y) * (height / deltaLat);
        return { x, y };
    }
    _pointToLatLng(point) {
        const topRight = this.#topRight, bottomLeft = this.#bottomLeft, deltaLat = -topRight.y + bottomLeft.y, deltaLng = topRight.x - bottomLeft.x;
        const { width, height } = this._pixelSize;
        // to normalized x
        const x = point.x / (width / deltaLng) + this.#bottomLeft.x;
        // to normalized y
        const y = point.y / (height / deltaLat) + this.#topRight.y;
        return this._worldNormalizedPointToLatLng({ x, y });
    }
    static _haversine(from, to) {
        const degToRad = MapProjectionConsts.RAD, lambda1 = from.lng * degToRad, lambda2 = to.lng * degToRad, phi1 = from.lat * degToRad, phi2 = to.lat * degToRad;
        return 2 * Math.asin(Math.sqrt(Math.pow(Math.sin((phi1 - phi2) / 2), 2) + Math.cos(phi1) * Math.cos(phi2) * Math.pow(Math.sin((lambda1 - lambda2) / 2), 2)));
    }
    /**
     * Computes a new LatLng by moving from a start position by a distance vector
     * expressed in meters along north (dNorth) and east (dEast), on a spherical Earth.
     * @param dEast Distance 'right' in meters.
     * @param dNorth Distance 'up' in meters.
     * @param start Start position.
     * @param radius Planet radius in meters.
     */
    static _offsetByMeters(start, dNorth, dEast, radius) {
        const { RAD: deg2Rad, DEG: rad2Deg } = MapProjectionConsts;
        // convert start lat/lng to radians
        const lat1 = start.lat * deg2Rad;
        const lng1 = start.lng * deg2Rad;
        // angular distance in radians
        const dLat = dNorth / radius;
        const dLng = dEast / (radius * Math.cos(lat1));
        const lat2 = lat1 + dLat;
        const lng2 = lng1 + dLng;
        return {
            lat: lat2 * rad2Deg,
            lng: lng2 * rad2Deg
        };
    }
    /**
     * Returns the position obtained by moving a geographic point by the specified amount (in meters) north and east.
     * @param start Starting position.
     * @param north Distance 'north' in meters.
     * @param east Distance 'east' in meters.
     * @param r Planet radius in meters (default is Earth's radius).
     * @returns New position after moving north and east by the specified distances.
     */
    static offsetBy(start, north, east, r = MapProjectionConsts.EARTH_RADIUS) {
        return MapProjection._offsetByMeters(start, north, east, r);
    }
    /**
     * Distance in meters between two locations.
     * @param from First location.
     * @param to Second location.
     */
    static distanceBetween(from, to, r = MapProjectionConsts.EARTH_RADIUS) {
        return MapProjection._haversine(from, to) * r;
    }
    static latLngToPoint(latLng, bounds, size) {
        return new MapProjection(size, bounds)._latLngToPoint(latLng);
    }
    static pointToLatLng(point, size, bounds) {
        return new MapProjection(size, bounds)._pointToLatLng(point);
    }
}
__decorate([
    Transformer(_MapConsts.TRANSFORMERS_NS + ".")
], MapProjection, "offsetBy", null);
__decorate([
    Transformer(_MapConsts.TRANSFORMERS_NS + ".")
], MapProjection, "distanceBetween", null);
__decorate([
    Transformer(_MapConsts.TRANSFORMERS_NS + ".")
], MapProjection, "latLngToPoint", null);
__decorate([
    Transformer(_MapConsts.TRANSFORMERS_NS + ".")
], MapProjection, "pointToLatLng", null);
// }
